import { Link } from 'react-router-dom';
import { MapPin, Users, Shield, Smartphone, CreditCard, MessageCircle } from 'lucide-react';

function LandingPage() {
  const heroImages = [
    "https://images.unsplash.com/photo-1618168140399-bed9cc74ab72?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjB2YWNhdGlvbiUyMHJlbnRhbHxlbnwwfHx8fDE3NTgxNjU3NTV8MA&ixlib=rb-4.1.0&q=85",
    "https://images.unsplash.com/photo-1651108066220-f61c22fc281f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwyfHx0cm9waWNhbCUyMHZpbGxhfGVufDB8fHx8MTc1ODE2NTc2MHww&ixlib=rb-4.1.0&q=85",
    "https://images.pexels.com/photos/59924/pexels-photo-59924.jpeg"
  ];

  const currentImage = heroImages[Math.floor(Math.random() * heroImages.length)];

  return (
    <div className="landing-page">
      {/* Hero Section */}
      <section className="hero-section">
        <img 
          src={currentImage} 
          alt="Luxury vacation rental" 
          className="hero-bg"
        />
        <div className="hero-content">
          <h1 className="hero-title">
            Luxury Timeshares at Premier Resorts
          </h1>
          <p className="hero-subtitle">
            Book world-class resort accommodations same-day to 21 days in advance. Perfect for 
            spontaneous luxury getaways when you're ready to escape to premier destinations.
          </p>
          <div className="hero-cta">
            <Link to="/properties" className="cta-primary">
              Book Amazing Getaway
            </Link>
            <Link to="/auth" className="cta-secondary">
              Join Today
            </Link>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section">
        <div className="container">
          <h2 className="section-title">Why Choose ZypeeTravel?</h2>
          <p className="section-subtitle">
            The exclusive platform for last-minute luxury timeshare rentals at world-class premier resorts
          </p>
          
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">
                <MapPin />
              </div>
              <h3 className="feature-title">Premier Resort Intelligence</h3>
              <p className="feature-description">
                Discover luxury attractions, fine dining, and exclusive events near your resort. 
                Smart travel intelligence to help you make the most of your premier getaway.
              </p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <MessageCircle />
              </div>
              <h3 className="feature-title">Luxury Concierge Service</h3>
              <p className="feature-description">
                Premium members enjoy personalized concierge assistance with exclusive recommendations 
                for fine dining, resort amenities, transportation, and unforgettable luxury experiences.
              </p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <Smartphone />
              </div>
              <h3 className="feature-title">Integrated Luxury Services</h3>
              <p className="feature-description">
                Get personalized recommendations for resort amenities, spa services, fine dining, and exclusive activities. 
                Your AI travel assistant optimizes your luxury resort experience.
              </p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <CreditCard />
              </div>
              <h3 className="feature-title">Transparent Pricing</h3>
              <p className="feature-description">
                Clear pricing: Timeshare owners pay 10% service fee. Travelers pay an additional $100 essential 
                resort preparation fee per booking (non-refundable). No hidden costs, no surprises.
              </p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <Users />
              </div>
              <h3 className="feature-title">Exclusive Resort Marketplace</h3>
              <p className="feature-description">
                Elite platform connecting luxury timeshare owners with discerning travelers 
                seeking world-class resort accommodations for spontaneous luxury escapes.
              </p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <Shield />
              </div>
              <h3 className="feature-title">Secure & Verified</h3>
              <p className="feature-description">
                Bank-level security with carefully verified luxury resort timeshares and secure payment processing. 
                Premier service standards for all travelers.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="pricing-section" style={{ padding: '100px 0', background: 'white' }}>
        <div className="container">
          <h2 className="section-title">Traveler Subscription Plans</h2>
          <p className="section-subtitle">
            Exclusive subscription plans designed for luxury resort travelers to maximize rewards and unlock premium features
          </p>
          
          <div className="pricing-grid" style={{ 
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: '32px',
            maxWidth: '800px',
            margin: '60px auto 0'
          }}>
            {/* Free Plan */}
            <div className="pricing-card" style={{
              background: 'white',
              padding: '32px',
              borderRadius: '20px',
              boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
              border: '2px solid #e2e8f0',
              textAlign: 'center',
              transition: 'all 0.3s ease'
            }}>
              <div style={{ color: '#6b7280', fontSize: '2rem', marginBottom: '16px' }}>🎯</div>
              <h3 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '16px', color: '#2d3748' }}>
                Free Loyalty
              </h3>
              <div style={{ fontSize: '3rem', fontWeight: '700', color: '#6b7280', marginBottom: '8px' }}>
                $0
              </div>
              <p style={{ color: '#718096', marginBottom: '24px' }}>per month</p>
              <div style={{ 
                background: '#f7fafc', 
                padding: '12px', 
                borderRadius: '8px',
                marginBottom: '24px'
              }}>
                <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#6b7280' }}>Basic Access</div>
                <div style={{ fontSize: '0.875rem', color: '#718096' }}>Standard Features</div>
              </div>
              
              <ul style={{ textAlign: 'left', marginBottom: '32px', listStyle: 'none', padding: 0 }}>
                <li style={{ padding: '6px 0', color: '#4a5568' }}>✓ Basic loyalty points</li>
                <li style={{ padding: '6px 0', color: '#4a5568' }}>✓ Standard support</li>
                <li style={{ padding: '6px 0', color: '#4a5568' }}>✓ Basic booking features</li>
              </ul>
              
              <Link to="/auth" className="btn btn-outline" style={{ width: '100%' }}>
                Get Started Free
              </Link>
            </div>

            {/* Premium Traveller's Plan */}
            <div className="pricing-card" style={{
              background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
              padding: '32px',
              borderRadius: '20px',
              boxShadow: '0 20px 40px rgba(16, 185, 129, 0.3)',
              border: 'none',
              textAlign: 'center',
              color: 'white',
              position: 'relative'
            }}>
              <div style={{
                position: 'absolute',
                top: '-12px',
                left: '50%',
                transform: 'translateX(-50%)',
                background: '#ffd700',
                color: '#2d3748',
                padding: '4px 16px',
                borderRadius: '12px',
                fontSize: '0.875rem',
                fontWeight: '600'
              }}>
                🤖 AI CONCIERGE INCLUDED
              </div>
              
              <div style={{ fontSize: '2rem', marginBottom: '16px' }}>👑</div>
              <h3 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '16px' }}>
                Premium Traveller's Plan
              </h3>
              <div style={{ fontSize: '3rem', fontWeight: '700', marginBottom: '8px' }}>
                $9.99
              </div>
              <p style={{ opacity: '0.9', marginBottom: '24px' }}>per month</p>
              <div style={{ 
                background: 'rgba(255, 255, 255, 0.2)', 
                padding: '12px', 
                borderRadius: '8px',
                marginBottom: '24px'
              }}>
                <div style={{ fontSize: '1.5rem', fontWeight: '700' }}>Complete Experience</div>
                <div style={{ fontSize: '0.875rem', opacity: '0.9' }}>AI + Premium Perks</div>
              </div>
              
              <ul style={{ textAlign: 'left', marginBottom: '32px', listStyle: 'none', padding: 0 }}>
                <li style={{ padding: '6px 0' }}>🤖 Personal Luxury Concierge</li>
                <li style={{ padding: '6px 0' }}>🍽️ Fine dining & resort recommendations</li>
                <li style={{ padding: '6px 0' }}>🚗 Premium transportation guidance</li>
                <li style={{ padding: '6px 0' }}>⚡ Priority concierge support</li>
                <li style={{ padding: '6px 0' }}>🏆 All loyalty tiers access</li>
                <li style={{ padding: '6px 0' }}>🎯 Earn loyalty points on stays</li>
                <li style={{ padding: '6px 0' }}>🏨 Exclusive resort timeshares</li>
              </ul>
              
              <Link to="/auth" className="btn" style={{ 
                width: '100%',
                background: 'white',
                color: '#10B981',
                fontWeight: '600'
              }}>
                Get Premium Plan
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Loyalty Add-ons Section */}
      <section style={{ 
        padding: '80px 0', 
        background: 'linear-gradient(135deg, #f7fafc 0%, #edf2f7 100%)',
        textAlign: 'center'
      }}>
        <div className="container">
          <h2 style={{ fontSize: '2.25rem', fontWeight: '700', marginBottom: '16px', color: '#2d3748' }}>
            Maximize Your Rewards
          </h2>
          <p style={{ fontSize: '1.1rem', marginBottom: '48px', color: '#4a5568', maxWidth: '600px', margin: '0 auto 48px' }}>
            Add loyalty point multipliers to your Premium Traveller's Plan for even more rewards
          </p>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '24px', maxWidth: '900px', margin: '0 auto' }}>
            {/* Plus Add-on */}
            <div style={{
              background: 'white',
              padding: '24px',
              borderRadius: '16px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
              border: '2px solid #e2e8f0'
            }}>
              <div style={{ fontSize: '1.5rem', marginBottom: '12px' }}>⚡</div>
              <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '8px', color: '#2d3748' }}>
                Plus Add-on
              </h3>
              <div style={{ fontSize: '2rem', fontWeight: '700', color: '#10B981', marginBottom: '8px' }}>
                +$3.99
              </div>
              <p style={{ color: '#4a5568', marginBottom: '16px' }}>per month</p>
              <div style={{ 
                background: '#f0fdf4', 
                padding: '8px', 
                borderRadius: '8px',
                marginBottom: '16px'
              }}>
                <div style={{ fontSize: '1.25rem', fontWeight: '600', color: '#15803d' }}>2X Points</div>
                <div style={{ fontSize: '0.875rem', color: '#166534' }}>Double loyalty rewards</div>
              </div>
            </div>

            {/* SuperPlus Add-on */}
            <div style={{
              background: 'white',
              padding: '24px',
              borderRadius: '16px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
              border: '2px solid #fbbf24'
            }}>
              <div style={{ fontSize: '1.5rem', marginBottom: '12px' }}>⭐</div>
              <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '8px', color: '#2d3748' }}>
                SuperPlus Add-on
              </h3>
              <div style={{ fontSize: '2rem', fontWeight: '700', color: '#f59e0b', marginBottom: '8px' }}>
                +$5.99
              </div>
              <p style={{ color: '#4a5568', marginBottom: '16px' }}>per month</p>
              <div style={{ 
                background: '#fef3c7', 
                padding: '8px', 
                borderRadius: '8px',
                marginBottom: '16px'
              }}>
                <div style={{ fontSize: '1.25rem', fontWeight: '600', color: '#d97706' }}>3X Points</div>
                <div style={{ fontSize: '0.875rem', color: '#b45309' }}>Triple loyalty rewards</div>
              </div>
            </div>

            {/* UltraPlus Add-on */}
            <div style={{
              background: 'white',
              padding: '24px',
              borderRadius: '16px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
              border: '2px solid #8b5cf6'
            }}>
              <div style={{ fontSize: '1.5rem', marginBottom: '12px' }}>💎</div>
              <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '8px', color: '#2d3748' }}>
                UltraPlus Add-on
              </h3>
              <div style={{ fontSize: '2rem', fontWeight: '700', color: '#8b5cf6', marginBottom: '8px' }}>
                +$7.99
              </div>
              <p style={{ color: '#4a5568', marginBottom: '16px' }}>per month</p>
              <div style={{ 
                background: '#f3e8ff', 
                padding: '8px', 
                borderRadius: '8px',
                marginBottom: '16px'
              }}>
                <div style={{ fontSize: '1.25rem', fontWeight: '600', color: '#7c3aed' }}>5X Points</div>
                <div style={{ fontSize: '0.875rem', color: '#6d28d9' }}>Maximum loyalty rewards</div>
              </div>
            </div>
          </div>

          <div style={{ 
            marginTop: '32px',
            padding: '20px',
            background: 'linear-gradient(135deg, #10B981, #059669)',
            borderRadius: '12px',
            color: 'white',
            maxWidth: '600px',
            margin: '32px auto 0'
          }}>
            <p style={{ fontSize: '1rem', margin: 0 }}>
              💡 <strong>Pro Tip:</strong> Add-ons stack with your Premium Traveller's Plan to maximize points for multiple bookings and enhance your loyal member experience!
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section style={{ 
        padding: '100px 0', 
        background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
        color: 'white',
        textAlign: 'center'
      }}>
        <div className="container">
          <h2 style={{ fontSize: '2.5rem', fontWeight: '700', marginBottom: '24px' }}>
            Ready to Start Your Journey?
          </h2>
          <p style={{ fontSize: '1.25rem', marginBottom: '40px', opacity: '0.9', maxWidth: '600px', margin: '0 auto 40px' }}>
            Join discerning travelers and luxury timeshare owners who trust ZypeeTravel for premier resort experiences.
          </p>
          <div style={{ display: 'flex', gap: '20px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/properties" className="btn" style={{
              background: 'rgba(255, 255, 255, 0.2)',
              color: 'white',
              padding: '16px 32px',
              borderRadius: '30px',
              textDecoration: 'none',
              fontWeight: '600',
              fontSize: '1.1rem',
              border: '2px solid rgba(255, 255, 255, 0.3)',
              backdropFilter: 'blur(10px)',
              transition: 'all 0.3s ease'
            }}>
              Find Properties
            </Link>
            <Link to="/auth" className="btn" style={{
              background: 'white',
              color: '#10B981',
              padding: '16px 32px',
              borderRadius: '30px',
              textDecoration: 'none',
              fontWeight: '600',
              fontSize: '1.1rem',
              transition: 'all 0.3s ease'
            }}>
              List Your Property
            </Link>
          </div>
        </div>
      </section>

      {/* Disclaimer Footer */}
      <section style={{ 
        padding: '40px 0', 
        background: '#f7fafc',
        borderTop: '1px solid #e2e8f0'
      }}>
        <div className="container">
          <div style={{ 
            background: 'white',
            padding: '24px',
            borderRadius: '12px',
            border: '1px solid #e2e8f0'
          }}>
            <h3 style={{ 
              fontSize: '1.1rem', 
              fontWeight: '600', 
              color: '#2d3748',
              marginBottom: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              ⚠️ Important Disclaimer
            </h3>
            <p style={{ 
              fontSize: '0.875rem', 
              color: '#4a5568',
              lineHeight: '1.6',
              margin: 0
            }}>
              <strong>ZypeeTravel LLC</strong> operates solely as a marketplace platform 
              connecting property owners with travelers. We are <strong>NOT responsible or liable</strong> for any 
              injuries, theft, damages, or misfortunes that occur at any property. All properties are independently 
              owned and operated. Users book and stay at properties at their own risk. ZypeeTravel makes no warranties 
              about property safety, security, or condition.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}

export default LandingPage;